import { useState } from 'react';

import { Button, Modal, notification, Tabs } from 'antd';
import { isSharedIdentificationFeatureEnabled } from 'constant';
import { useCreateRelationGroupMutation, useEditRelationGroupMutation } from 'services';

import ArticleCreateForm from './article-create-form';
import ArticleEditForm from './article-edit-form';
import GroupMemberList from './group-member-list';
import SelectGroup from './select-group';
import SelectGroupPrompt from './select-group/select-group-prompt';
import GroupEmptyState from './select-group/group-empty-state';
import { isPostOrder } from './share-identification';

/**
 * BE grubu kaydedip yalnız kimlik kartı ortaklaştırmasını yapamadığında `409` döner (ör. geride kalan madde ya da
 * ana maddenin kartı yok). Grup kaydedildiği için pencere kapanır; RTK hatalı yanıtta da grup cache'ini tazeler.
 */
const SHARE_IDENTIFICATION_FAILED_STATUS = 409;

// BE iş kuralı mesajını API Platform hata gövdesinde `hydra:description` ile döner. Metin JSX'te basıldığı için
// HTML kaçıran `getApiErrorMessage` kullanılmaz (kesme işareti `&#39;` görünürdü).
const getErrorDetail = (error) => error.data?.['hydra:description'] ?? error.data?.detail;

// BE mesajı geride kalan maddeleri alt alta listeler; satır sonları korunur, kullanıcı okuyana kadar açık kalır.
const notifyShareIdentificationFailed = (error) =>
  notification.warning({
    message: 'Grup kaydedildi, kimlik kartı ortaklaştırılamadı',
    description: (
      <span style={{ whiteSpace: 'pre-line' }}>
        {getErrorDetail(error) ?? 'Kimlik kartı ortaklaştırılırken bir hata oluştu.'}
      </span>
    ),
    duration: 0,
  });

/**
 * Madde grubu oluşturma, seçme ve düzenleme modal'ı.
 * @param {Object} props
 * @param {'create' | 'select' | 'edit'} props.mode - Modal modu
 * @param {boolean} props.open - Modal açık mı
 * @param {Function} props.onClose - Modal kapatma callback
 * @param {Object} [props.initialData] - Edit modunda grubun kimliği { id }
 * @param {{ offerId: string, offerTitle: string, offerNumericId: number, offerStatus: number, offerStep: number }} [props.autoAddArticle] - Otomatik eklenecek madde bilgisi
 */
function ArticleGroupModal({ mode, open, onClose, initialData, autoAddArticle }) {
  const [activeTab, setActiveTab] = useState('create');
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [createRelationGroup, { isLoading: isCreating }] = useCreateRelationGroupMutation();
  const [editRelationGroup, { isLoading: isEditing }] = useEditRelationGroupMutation();

  const isLoading = isCreating || isEditing;

  // Kartı ortak gruba Telif Sonrası'nda olmayan madde eklenemez; BE de aynı `PUT`'u `400` ile reddeder.
  const postOrderOnlyGroups =
    isSharedIdentificationFeatureEnabled && Boolean(autoAddArticle) && !isPostOrder({ step: autoAddArticle.offerStep });

  const handleClose = () => {
    setActiveTab('create');
    setSelectedGroup(null);
    onClose();
  };

  const handleCreate = async (body) => {
    const { error } = await createRelationGroup({ body });
    if (error?.status === SHARE_IDENTIFICATION_FAILED_STATUS) {
      notifyShareIdentificationFailed(error);
      return handleClose();
    }
    if (error) {
      return notification.error({
        message: 'Grup oluşturulamadı',
        description: getErrorDetail(error) ?? 'Bir hata oluştu, lütfen tekrar deneyiniz.',
      });
    }
    notification.success({ message: 'Grup başarıyla oluşturuldu.' });
    handleClose();
  };

  const handleEdit = async (body) => {
    const groupId = initialData?.id ?? selectedGroup?.id;
    const { error } = await editRelationGroup({ id: groupId, body });
    if (error?.status === SHARE_IDENTIFICATION_FAILED_STATUS) {
      notifyShareIdentificationFailed(error);
      return handleClose();
    }
    if (error) {
      return notification.error({
        message: 'Grup güncellenemedi',
        description: getErrorDetail(error) ?? 'Bir hata oluştu, lütfen tekrar deneyiniz.',
      });
    }
    notification.success({ message: 'Grup başarıyla güncellendi.' });
    handleClose();
  };

  const handleAddToGroup = async () => {
    if (!selectedGroup || !autoAddArticle) return;
    const existingMembers = selectedGroup.members.map((member, index) => ({
      articleOffer: member['@id'],
      position: index + 1,
      ...(member.memberId && { id: member.memberId }),
    }));
    const body = {
      name: selectedGroup.name,
      members: [...existingMembers, { articleOffer: autoAddArticle.offerId, position: existingMembers.length + 1 }],
    };
    const { error } = await editRelationGroup({ id: selectedGroup.id, body });
    if (error) {
      return notification.error({
        message: 'Gruba eklenemedi',
        description: getErrorDetail(error) ?? 'Bir hata oluştu, lütfen tekrar deneyiniz.',
      });
    }
    notification.success({ message: 'Madde gruba başarıyla eklendi.' });
    handleClose();
  };

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
  };

  const getTitle = () => {
    if (mode === 'edit') return 'Grubu Düzenle';
    if (mode === 'select') return 'Madde Grubuna Ekle';
    return 'Yeni Grup Oluştur';
  };

  const renderContent = () => {
    if (mode === 'create') {
      return <ArticleCreateForm onSubmit={handleCreate} autoAddArticle={autoAddArticle} />;
    }

    if (mode === 'edit') {
      return <ArticleEditForm groupId={initialData.id} onSubmit={handleEdit} />;
    }

    // mode === 'select' → tabs (park edilmiş)
    return (
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          setSelectedGroup(null);
        }}
        items={[
          {
            key: 'create',
            label: 'Grup Oluştur',
            children: <ArticleCreateForm onSubmit={handleCreate} autoAddArticle={autoAddArticle} />,
          },
          {
            key: 'select',
            label: 'Grup Seç',
            children: (
              <div className="gx-d-flex gx-flex-column" style={{ gap: '1rem' }}>
                <SelectGroup onGroupSelect={handleGroupSelect} postOrderOnly={postOrderOnlyGroups} />
                {!selectedGroup && <SelectGroupPrompt />}
                {selectedGroup && selectedGroup.members.length === 0 && <GroupEmptyState />}
                {selectedGroup && selectedGroup.members.length > 0 && <GroupMemberList members={selectedGroup.members} />}
              </div>
            ),
          },
        ]}
      />
    );
  };

  return (
    <Modal
      title={getTitle()}
      open={open}
      onCancel={handleClose}
      width={720}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Vazgeç
        </Button>,
        mode === 'select' && activeTab === 'select' ? (
          <Button
            key="submit"
            type="primary"
            loading={isLoading}
            disabled={!selectedGroup}
            onClick={handleAddToGroup}
          >
            Kaydet
          </Button>
        ) : (
          <Button
            key="submit"
            type="primary"
            loading={isLoading}
            htmlType="submit"
            form="article-group-form"
          >
            Kaydet
          </Button>
        ),
      ]}
    >
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {renderContent()}
      </div>
    </Modal>
  );
}

export default ArticleGroupModal;
